import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ScanLine, HelpCircle, Book, User, Search } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { setScanModalOpen, setCurrentBook } from '../store/slices/booksSlice';
import { setCurrentMember } from '../store/slices/membersSlice';

const ScanModal = ({ isOpen, onClose, onBookFound, onMemberFound }) => {
    const dispatch = useDispatch();
    const { items: books } = useSelector((state) => state.books);
    const { items: members } = useSelector((state) => state.members);
    const [manualBookId, setManualBookId] = useState('');
    const [manualMemberId, setManualMemberId] = useState('');
    const [manualCloeeId, setManualCloeeId] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const scanTimeoutRef = useRef(null);

    if (!isOpen) return null;

    const handleIdSearch = (id, type) => {
        if (!id) return;

        // Reset error message on new search attempt
        setErrorMessage('');

        if (type === 'book') {
            const book = books.find(b => (b.item_id || b.id) == id);
            if (book) {
                dispatch(setCurrentBook(book));
                dispatch(setScanModalOpen(false));
                if (onBookFound) onBookFound();
            } else {
                setErrorMessage("Livre non trouvé");
            }
        } else if (type === 'member') {
            const member = members.find(m => (m.ID || m.id) == id);
            if (member) {
                dispatch(setCurrentMember(member));
                dispatch(setScanModalOpen(false));
                if (onMemberFound) onMemberFound();
            } else {
                setErrorMessage("Membre non trouvé");
            }
        }
        else if (type === 'cloee') {
            const member = members.find(m => (m.email.split('.')[1].split('@')[0]) == id);
            if (member) {
                dispatch(setCurrentMember(member));
                dispatch(setScanModalOpen(false));
                if (onMemberFound) onMemberFound();
            } else {
                setErrorMessage("Membre non trouvé");
            }
        }
        setManualBookId('');
        setManualMemberId('');
        setManualCloeeId('');
    };

    const handleScan = (e) => {
        const value = e.target.value;

        if (scanTimeoutRef.current) {
            clearTimeout(scanTimeoutRef.current);
        }

        scanTimeoutRef.current = setTimeout(() => {
            if (value.startsWith('ITEM:')) {
                handleIdSearch(value.split(':')[1], 'book');
            } else if (value.startsWith('USER:')) {
                handleIdSearch(value.split(':')[1], 'member');
            }
        }, 300);
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm lg:pl-72">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-slate-900 border border-white/10 rounded-2xl p-6 w-full max-w-md shadow-2xl text-center"
            >
                <div className="flex justify-end mb-2">
                    <button onClick={onClose} className="text-slate-400 hover:text-white">
                        <X size={20} />
                    </button>
                </div>

                <div className="bg-black rounded-xl mb-6 relative overflow-hidden border-2 border-primary/30 flex flex-col">
                    <div className="relative flex items-center p-4 z-10">
                        <ScanLine className="text-primary mr-3 animate-pulse" size={24} />
                        <input
                            type="text"
                            placeholder="Scanner le Qr code ..."
                            className="w-full bg-transparent border-none focus:outline-none text-white font-mono text-lg placeholder:text-slate-600"
                            autoFocus
                            onChange={handleScan}
                        />
                    </div>
                    {errorMessage && (
                        <div className="bg-red-500/20 text-red-400 p-2 text-sm font-bold border-t border-red-500/20 animate-pulse">
                            {errorMessage}
                        </div>
                    )}

                    <div className="px-4 py-4 flex flex-col gap-4 border-t border-white/10 bg-white/5 relative z-10">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-blue-500/30 flex items-center justify-center text-blue-400 flex-shrink-0">
                                <Book size={18} />
                            </div>
                            <input
                                type="text"
                                value={manualBookId}
                                placeholder="ID Livre..."
                                className="flex-1 bg-slate-800/50 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500/50 transition-colors font-medium"
                                onChange={(e) => setManualBookId(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleIdSearch(manualBookId, 'book')}
                            />
                            <button
                                onClick={() => handleIdSearch(manualBookId, 'book')}
                                className="p-2 bg-blue-500/30 text-blue-300 rounded-lg hover:bg-blue-500 hover:text-white transition-all shadow-lg shadow-blue-500/10"
                            >
                                <Search size={18} />
                            </button>
                        </div>

                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-purple-500/30 flex items-center justify-center text-purple-400 flex-shrink-0">
                                <User size={18} />
                            </div>
                            <input
                                type="text"
                                value={manualMemberId}
                                placeholder="ID Membre..."
                                className="flex-1 bg-slate-800/50 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-500/50 transition-colors font-medium"
                                onChange={(e) => setManualMemberId(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleIdSearch(manualMemberId, 'member')}
                            />
                            <button
                                onClick={() => handleIdSearch(manualMemberId, 'member')}
                                className="p-2 bg-purple-500/30 text-purple-300 rounded-lg hover:bg-purple-500 hover:text-white transition-all shadow-lg shadow-purple-500/10"
                            >
                                <Search size={18} />
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-purple-500/30 flex items-center justify-center text-purple-400 flex-shrink-0">
                                <User size={18} />
                            </div>
                            <input
                                type="text"
                                value={manualCloeeId}
                                placeholder="ID CLOEE"
                                className="flex-1 bg-slate-800/50 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-500/50 transition-colors font-medium"
                                onChange={(e) => setManualCloeeId(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleIdSearch(manualCloeeId, 'cloee')}
                            />
                            <button
                                onClick={() => handleIdSearch(manualCloeeId, 'cloee')}
                                className="p-2 bg-purple-500/30 text-purple-300 rounded-lg hover:bg-purple-500 hover:text-white transition-all shadow-lg shadow-purple-500/10"
                            >
                                <Search size={18} />
                            </button>
                        </div>
                    </div>

                    <div className="absolute inset-0 bg-primary/5 pointer-events-none animate-scan" />
                </div>

                <h5 style={{ fontSize: '1.1rem' }} className="text-xl font-bold text-white mb-2">Scanner ou rechercher manuellement</h5>
                <p className="text-slate-400 mb-6 text-sm">Saisissez l'identifiant pour accéder aux détails.</p>
            </motion.div>
        </div>
    );
};

export default ScanModal;
